const p = (n) => String(n).padStart(2, "0");

export const stamp = (d = new Date()) =>
  `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
export const fileStamp = (d = new Date()) => stamp(d).replace(" ", "_").replace(/:/g, "");
export const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const tzAbbr = (d = new Date()) =>
  d.toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ").pop();
export const uuid = () => crypto.randomUUID();
export const ymd = (d) => stamp(d).slice(0, 10);

export function fmtBytes(b = 0) {
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(i ? 1 : 0)} ${u[i]}`;
}

const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
export const sha256Text = async (s) => hex(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s)));
export async function sha256Blob(b) {
  try {
    return hex(await crypto.subtle.digest("SHA-256", await b.arrayBuffer()));
  } catch {
    return "unavailable-file-too-large-for-memory";
  }
}

export function kindOf(mime = "", name = "") {
  if (mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif|bmp|avif)$/i.test(name)) return "photo";
  if (/\.(mp4|mov|m4v|webm|mkv|avi|3gp)$/i.test(name) && !mime.startsWith("audio/")) return "video";
  if (/\.(mp3|m4a|wav|ogg|oga|aac|flac|opus)$/i.test(name)) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  if (mime === "text/plain" || /\.txt$/i.test(name)) return "text";
  return "file";
}

export function extOf(name = "", mime = "") {
  const m = name.match(/\.([a-zA-Z0-9]{1,6})$/);
  if (m) return m[1].toLowerCase();
  return (mime.split("/")[1] || "bin").replace("jpeg", "jpg").replace(/[^a-z0-9]/g, "");
}

export const blobToDataUrl = (blob) =>
  new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const pascal = (s, max = 40) =>
  (s || "").replace(/[^a-zA-Z0-9]+/g, " ").trim().split(" ").filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1)).join("").slice(0, max);

export function identifier(profile) {
  if (profile?.apply?.filenames) {
    if (profile.stamp?.initials && profile.initials) return pascal(profile.initials, 10).toUpperCase();
    if (profile.stamp?.name && profile.fullName) return pascal(profile.fullName, 24);
  }
  return "StarVault";
}

export function docName({ profile, folderName, title, ref, date, ext, suffix }) {
  const parts = [
    identifier(profile),
    pascal(folderName) || "Folder",
    pascal(title) || "Note",
    (ref || "").replace(/[^a-zA-Z0-9-]+/g, "-").replace(/^-|-$/g, ""),
    fileStamp(date ? new Date(date) : new Date()),
  ];
  if (suffix) parts.push(suffix);
  return `${parts.filter(Boolean).join("_")}.${ext}`;
}

export const MIME_EXT = {
  "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif", "image/webp": "webp",
  "image/heic": "heic", "text/plain": "txt", "audio/mpeg": "mp3", "audio/ogg": "ogg", "audio/mp4": "m4a",
  "audio/wav": "wav", "video/mp4": "mp4", "video/webm": "webm",
};
const EXT_MIME = { ...Object.fromEntries(Object.entries(MIME_EXT).map(([m, e]) => [e, m])), jpeg: "image/jpeg", mov: "video/quicktime" };

export async function sniffType(blob) {
  const b = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  const hex = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  const s = String.fromCharCode(...b);
  if (s.startsWith("%PDF")) return "application/pdf";
  if (hex.startsWith("ffd8ff")) return "image/jpeg";
  if (hex.startsWith("89504e47")) return "image/png";
  if (s.startsWith("GIF8")) return "image/gif";
  if (s.startsWith("RIFF") && s.slice(8, 12) === "WEBP") return "image/webp";
  if (s.startsWith("RIFF") && s.slice(8, 12) === "WAVE") return "audio/wav";
  if (s.slice(4, 8) === "ftyp") {
    const brand = s.slice(8, 12);
    if (/heic|heix|mif1|msf1/.test(brand)) return "image/heic";
    return /M4A/.test(brand) ? "audio/mp4" : "video/mp4";
  }
  if (s.startsWith("ID3") || hex.startsWith("fffb")) return "audio/mpeg";
  if (s.startsWith("OggS")) return "audio/ogg";
  if (hex.startsWith("1a45dfa3")) return "video/webm";
  const t = new Uint8Array(await blob.slice(0, 1024).arrayBuffer());
  if (t.length && t.every((c) => c === 9 || c === 10 || c === 13 || c >= 32)) return "text/plain";
  return "";
}

export async function normalizeFile(file) {
  const ext = ((file.name || "").match(/\.([a-z0-9]{2,5})$/i) || [])[1]?.toLowerCase();
  let type = file.type && file.type !== "application/octet-stream" ? file.type : "";
  if (!type) type = (await sniffType(file)) || EXT_MIME[ext] || "application/octet-stream";
  let name = file.name || "file";
  if (!ext && MIME_EXT[type]) name = `${name}.${MIME_EXT[type]}`;
  if (type === file.type && name === file.name) return file;
  return new File([file], name, { type, lastModified: file.lastModified || Date.now() });
}
